import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import WatchAdModal from "../components/WatchAdModal";
import { useAuth } from "../components/ProtectedRoute";

const API = ""; // same-origin now — Vercel rewrites /api/* to the backend (see client/vercel.json). Keeps the session cookie first-party for Safari/Firefox.

/* ─────────────────────────────────────────
   AD COMPONENTS
───────────────────────────────────────── */

// 1. NativeBanner — content ke beech, natural lage
function NativeBannerAd() {
  const ref = useRef(null);
  useEffect(() => {
    if (!ref.current || ref.current.querySelector("script")) return;
    const s = document.createElement("script");
    s.async = true;
    s.setAttribute("data-cfasync", "false");
    s.src = "https://pl29294093.profitablecpmratenetwork.com/aea55a3d3081801464262325e2e33cc7/invoke.js";
    ref.current.appendChild(s);
  }, []);
  return (
    <div style={{ marginBottom: 20 }}>
      <span style={{ display: "block", color: "#38383F", fontSize: 9, textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 }}>
        Sponsored
      </span>
      <div ref={ref} id="container-aea55a3d3081801464262325e2e33cc7" />
    </div>
  );
}

// 2. SocialBar — page load pe ek baar inject, body mein
function useSocialBarAd(show) {
  useEffect(() => {
    if (!show) return;
    if (document.getElementById("socialbar-script")) return;
    const s = document.createElement("script");
    s.id = "socialbar-script";
    s.src = "https://pl29294096.profitablecpmratenetwork.com/2d/fb/08/2dfb0845e3243c7379d1f4333d482518.js";
    document.body.appendChild(s);
  }, [show]);
}

// 3. Banner 300x250 — sidebar ke andar
function Banner300x250() {
  const ref = useRef(null);
  useEffect(() => {
    if (!ref.current || ref.current.querySelector("script")) return;
    // atOptions set
    window.atOptions = {
      key: "860bf61368ab91237ab740096195b29e",
      format: "iframe",
      height: 250,
      width: 300,
      params: {}
    };
    const s = document.createElement("script");
    s.src = "https://www.highperformanceformat.com/860bf61368ab91237ab740096195b29e/invoke.js";
    ref.current.appendChild(s);
  }, []);
  return (
    <div style={{
      marginTop: 16,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: 4,
    }}>
      <span style={{ color: "#38383F", fontSize: 9, textTransform: "uppercase", letterSpacing: 1 }}>Advertisement</span>
      <div ref={ref} style={{ width: 300, height: 250, overflow: "hidden", borderRadius: 8 }} />
    </div>
  );
}

/* ─────────────────────────────────────────
   LOGO
───────────────────────────────────────── */
function SubAILogo() {
  return (
    <div style={{ textAlign: "center" }}>
      <span style={{ fontSize: 24, fontWeight: 800, letterSpacing: 1 }}>
        <span style={{ color: "#F5F5F7" }}>Sub</span>
        <span style={{
          background: "linear-gradient(90deg, #7C5CFF, #FFC966)",
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
          marginLeft: 2
        }}>AI</span>
      </span>
      <div style={{ fontSize: 9, color: "#F5F5F7", marginTop: 4, letterSpacing: 3 }}>
        SMARTER • FASTER • BETTER
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────
   MAIN DASHBOARD
───────────────────────────────────────── */
/* ══════════════════════════════════════
   TRANSLATION SETTINGS
   Every option here maps to something the backend really does (see GET /api/models):
     * Quality preset  -> batch size (lines per request) + sampling temperature
     * Batch size      -> lines per request; slider max = the limit the server enforces for the provider
     * Pacing          -> the gap the client waits between provider requests
     * Retry           -> fixed by the server: 1 automatic retry per chunk (2 provider calls max)
   There are no accuracy / speed percentages: those were never measured.
══════════════════════════════════════ */
const SETTINGS_STORAGE_KEY = "subai_translate_settings";
const GEMINI_STORAGE_KEY   = "subai_gemini_model";
const DEFAULT_BATCH_LIMIT  = { min: 1, max: 40 };   // used only until /api/models answers (40 is accepted by every provider)

function TranslationSettings({ cfg, provider, quality, onQuality, batchSize, onBatch, pacing, onPacing, limit }) {
  const presets = cfg?.quality ? Object.values(cfg.quality) : [];
  const pacings = cfg?.pacing || [];
  const gapSecOf = (p) => ((provider === "openrouter" ? p.openrouterGapMs : p.gapMs) || 0) / 1000;
  const activePreset = cfg?.quality?.[quality];
  const activePacing = pacings.find(p => p.id === pacing);
  const gapSec       = activePacing ? gapSecOf(activePacing) : null;
  const requestsPer1000 = Math.ceil(1000 / Math.max(1, batchSize));
  const minMinutes = gapSec ? Math.ceil(((requestsPer1000 - 1) * gapSec) / 60) : null;
  const maxCalls   = cfg?.policy?.maxProviderCallsPerChunk ?? 2;
  const maxRetries = cfg?.policy?.maxAutoRetries ?? 1;

  const chip = (active, color = "#7C5CFF") => ({
    flex: 1, padding: "8px 4px", borderRadius: 8, cursor: "pointer", transition: "all 0.2s",
    border: `1px solid ${active ? color : "#26262E"}`,
    background: active ? color + "22" : "transparent",
    color: active ? color : "#6E6E78",
    fontSize: 11, fontWeight: active ? 700 : 500, lineHeight: 1.3,
  });
  const caption = { color: "#57575F", fontSize: 10.5, margin: "6px 0 0", lineHeight: 1.5 };
  const heading = { color: "#86868F", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.8, margin: "14px 0 6px" };

  return (
    <div style={{ background: "#18181F", border: "1px solid #26262E", borderRadius: 12, padding: "14px 16px", marginTop: 14 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
        <span style={{ color: "#7C5CFF", fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1 }}>
          ⚙️ Translation Settings
        </span>
        <span style={{ color: "#57575F", fontSize: 11 }}>{batchSize} lines / request</span>
      </div>

      {/* Quality presets */}
      <p style={{ ...heading, marginTop: 8 }}>Quality preset</p>
      {presets.length > 0 ? (
        <>
          <div style={{ display: "flex", gap: 6 }}>
            {presets.map(p => (
              <button key={p.id} type="button" onClick={() => { onQuality(p.id); onBatch(Math.min(p.batchSize, limit.max)); }} style={chip(quality === p.id)}>
                {p.id === "accurate" ? "🎯" : p.id === "fast" ? "⚡" : "⚖️"} {p.label}
              </button>
            ))}
          </div>
          {activePreset && <p style={caption}>{activePreset.summary}</p>}
        </>
      ) : (
        <p style={caption}>Preset list server se load nahi hui — Balanced defaults use honge. Neeche batch size manually set kar sakte ho.</p>
      )}

      {/* Batch size */}
      <p style={heading}>Batch size</p>
      <input
        type="range" min={limit.min} max={limit.max} value={Math.min(batchSize, limit.max)}
        onChange={e => onBatch(Number(e.target.value))}
        style={{ width: "100%", accentColor: "#7C5CFF", cursor: "pointer" }}
      />
      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 2 }}>
        <span style={{ color: "#4A4A52", fontSize: 10 }}>{limit.min} line</span>
        <span style={{ color: "#4A4A52", fontSize: 10 }}>{limit.max} lines (is provider ka max)</span>
      </div>
      <p style={caption}>
        ≈ {requestsPer1000} requests per 1000 subtitle lines.
        {minMinutes !== null && <> Pacing ki wajah se kam se kam ~{minMinutes} min (API ka response time alag se lagta hai).</>}
      </p>

      {/* Pacing */}
      <p style={heading}>Request pacing</p>
      {pacings.length > 0 ? (
        <>
          <div style={{ display: "flex", gap: 6 }}>
            {pacings.map(p => (
              <button key={p.id} type="button" onClick={() => onPacing(p.id)} style={chip(pacing === p.id, "#22D3C5")}>
                {p.label}<br /><span style={{ fontWeight: 400, fontSize: 10 }}>~{gapSecOf(p)}s gap</span>
              </button>
            ))}
          </div>
          {activePacing && <p style={caption}>{activePacing.summary}. Provider ka Retry-After hamesha is gap se upar maana jaata hai.</p>}
        </>
      ) : (
        <p style={caption}>Pacing options load nahi hue — Safe pacing (free-tier friendly) use hogi.</p>
      )}

      {/* Retry — fixed by the server, shown read-only */}
      <p style={heading}>Retry</p>
      <p style={{ ...caption, margin: 0, color: "#86868F" }}>
        {maxRetries} automatic retry per chunk (max {maxCalls} provider calls). Fallback / repair calls isi limit mein count hoti hain — koi hidden extra retry nahi.
      </p>

      <p style={{ ...caption, marginTop: 12, color: "#4A4A52" }}>
        Trade-off: chhota batch = zyada requests (RPM / quota zyada use) par har request mein kam lines; bada batch = kam requests, lekin ek chunk fail ho to zyada lines dobara bhejni padti hain.
        Kam temperature = kam random output. Ye settings speed / accuracy ki guarantee nahi hain — real result model aur subtitle par depend karta hai.
      </p>
    </div>
  );
}

/* Gemini model chooser. The list comes from the server's registry (the same one the backend validates against). */
function GeminiModelPicker({ cfg, mode, onMode, model, onModel, color }) {
  const registry = cfg?.registry || [];
  const auto     = cfg?.autoModels || [];
  const selected = registry.find(m => m.id === model);
  const groups   = [
    { tier: "stable",  label: "✅ Stable" },
    { tier: "preview", label: "🧪 Preview" },
    { tier: "legacy",  label: "📦 Legacy (limited access)" },
  ];
  const seg = (active) => ({
    flex: 1, padding: "8px 6px", borderRadius: 8, cursor: "pointer", fontSize: 12, fontWeight: active ? 700 : 500,
    border: `1px solid ${active ? color : "#26262E"}`, background: active ? color + "22" : "transparent", color: active ? color : "#9A9AA3",
  });
  return (
    <div style={{ ...styles.modelPickerWrap, borderColor: color + "33" }}>
      <label style={styles.modelPickerLabel}>✨ Gemini model</label>
      <div style={{ display: "flex", gap: 6, marginBottom: 8 }}>
        <button type="button" onClick={() => onMode("auto")} style={seg(mode === "auto")}>Auto</button>
        <button type="button" onClick={() => registry.length && onMode("manual")} disabled={!registry.length}
                style={{ ...seg(mode === "manual"), opacity: registry.length ? 1 : 0.5 }}>Manual</button>
      </div>

      {mode === "manual" && registry.length > 0 && (
        <>
          <select value={model || ""} onChange={e => onModel(e.target.value)} style={{ ...styles.select, width: "100%", fontSize: 12 }}>
            {groups.map(g => {
              const items = registry.filter(m => m.tier === g.tier);
              return items.length ? (
                <optgroup key={g.tier} label={g.label}>
                  {items.map(m => <option key={m.id} value={m.id}>{m.label}</option>)}
                </optgroup>
              ) : null;
            })}
          </select>
          <p style={styles.modelPickerNote}>
            🔒 Ye exact model use hoga — kabhi chupke se badla nahi jaayega. Agar ye model aapki key par available nahi hua to translation rukegi aur error dikhegi.
            {selected?.note ? ` (${selected.note})` : ""}
          </p>
        </>
      )}

      {mode === "auto" && (
        <p style={styles.modelPickerNote}>
          ⚡ Server verified list mein se model chunta hai{auto.length ? ` (order: ${auto.join(" → ")})` : ""}. Model tabhi badalta hai jab Google confirm kare ki wo available nahi hai — aur wo bhi retry limit ke andar. Live screen par asli model dikhega.
        </p>
      )}
      {!registry.length && <p style={styles.modelPickerNote}>Model list server se load nahi hui — Auto mode use hoga.</p>}
      {cfg?.verifiedAt && (
        <p style={{ ...styles.modelPickerNote, color: "#4A4A52" }}>Model list verified {cfg.verifiedAt} · ai.google.dev/gemini-api/docs/models</p>
      )}
    </div>
  );
}

// Provider metadata used by the animated provider-picker in the API key box.
// Adding a new provider is just one entry here plus its model <select> block.
const PROVIDERS = [
  { id: "gemini",     label: "Gemini (Google)", icon: "✨", color: "#E8B34D",
    keyPlaceholder: "Gemini API key paste karo...", keyLink: "aistudio.google.com/apikey",
    tagline: "Auto ya Manual model" },
  { id: "openrouter", label: "OpenRouter",       icon: "🌐", color: "#22D3C5",
    keyPlaceholder: "OpenRouter API key paste karo...", keyLink: "openrouter.ai/keys",
    tagline: "100+ models ek key se" },
  { id: "llama",      label: "Llama AI (Meta)",  icon: "🦙", color: "#5B6EE1",
    keyPlaceholder: "Llama API key paste karo...", keyLink: "llama.developer.meta.com",
    tagline: "auto model selection" },
  { id: "openai",     label: "OpenAI",           icon: "🤖", color: "#10a37f",
    keyPlaceholder: "OpenAI API key paste karo...", keyLink: "platform.openai.com/api-keys",
    tagline: "auto model selection" },
  { id: "agentrouter", label: "AgentRouter",      icon: "🧭", color: "#E8935A",
    keyPlaceholder: "AgentRouter API key paste karo...", keyLink: "agentrouter.org/console/token",
    tagline: "multi-provider gateway" },
];

export default function Dashboard() {
  const navigate = useNavigate();
  const auth = useAuth();
  const [files, setFiles] = useState([]);
  const [apiKey, setApiKey] = useState("");
  const [provider, setProvider] = useState("gemini");
  const [orModel, setOrModel] = useState("google/gemini-2.5-flash:free"); // OpenRouter model
  const [llamaModel, setLlamaModel] = useState("Llama-3.3-70B-Instruct");  // Llama AI (Meta, official) model
  const [openaiModel, setOpenaiModel] = useState("gpt-4o-mini");          // OpenAI model
  const [agentrouterModel, setAgentrouterModel] = useState("gpt-5.6-sol"); // AgentRouter model

  // Notification bell state
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifPanel, setShowNotifPanel] = useState(false);

  // Metadata for whichever provider is currently selected -- drives the key
  // placeholder, accent color, and info card below. Falls back to Gemini's
  // config if `provider` somehow doesn't match any entry.
  const activeProviderConfig = PROVIDERS.find(p => p.id === provider) || PROVIDERS[0];
  const [sourceLang, setSourceLang] = useState("auto"); // auto-detect default
  const readStored = (key) => { try { return JSON.parse(localStorage.getItem(key)); } catch { return null; } };
  const [batchSize, setBatchSize] = useState(() => {
    const b = Number(readStored(SETTINGS_STORAGE_KEY)?.batchSize);
    return Number.isInteger(b) && b >= 1 && b <= 80 ? b : 20;     // default: balanced (20 lines/request)
  });
  const [quality, setQuality] = useState(() => readStored(SETTINGS_STORAGE_KEY)?.quality || "balanced");
  const [pacing,  setPacing]  = useState(() => readStored(SETTINGS_STORAGE_KEY)?.pacing  || "safe");
  // Gemini model choice (Auto | Manual + model id). Remembered in this browser so it survives navigation / resume.
  const [geminiMode,  setGeminiMode]  = useState(() => readStored(GEMINI_STORAGE_KEY)?.mode === "manual" ? "manual" : "auto");
  const [geminiModel, setGeminiModel] = useState(() => readStored(GEMINI_STORAGE_KEY)?.model || "");
  const [modelCfg, setModelCfg] = useState(null);   // authoritative config from GET /api/models (registry, presets, pacing, limits)
  const [loading, setLoading] = useState(false);
  const [convertingId, setConvertingId] = useState(null);
  const [time, setTime] = useState(0);
  const [profile, setProfile] = useState(null);
  const [showKey, setShowKey] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [bugModalOpen, setBugModalOpen] = useState(false);
  const [bugMessage, setBugMessage] = useState("");
  const [bugSubmitting, setBugSubmitting] = useState(false);
  const [toasts, setToasts] = useState([]); // toast notifications

  // ── AD-GATE STATE (free tier) ──
  const [showAdModal,       setShowAdModal]       = useState(false);
  const [pendingConvertId,  setPendingConvertId]  = useState(null);

  // Toast helper
  const showToast = (msg, type = "info", duration = 4000) => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, msg, type }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), duration);
  };

  const token = localStorage.getItem("subai_session"); // non-sensitive "am I logged in" flag now — real session is the httpOnly cookie
  // Ultra Pro users kabhi ads nahi dekhenge — double safety check
  const showAds = profile?.showAds === true && profile?.plan !== "ultra_pro";

  // SocialBar — ek baar page pe inject
  useSocialBarAd(!!showAds);

  useEffect(() => {
    if (!token) { navigate("/"); return; }
    loadProfile();
    loadFiles();
    loadNotifications();
  }, []);

  // Model registry / presets / limits come from the server -- the same tables the backend validates against.
  useEffect(() => {
    let alive = true;
    fetch(`${API}/api/models`)
      .then(r => (r.ok ? r.json() : null))
      .then(data => { if (alive && data?.registry) setModelCfg(data); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  // Once the registry is known, repair stale saved choices instead of sending something the server would reject.
  useEffect(() => {
    if (!modelCfg) return;
    if (!modelCfg.quality?.[quality]) setQuality(modelCfg.defaultQuality || "balanced");
    if (!modelCfg.pacing?.some(p => p.id === pacing)) setPacing(modelCfg.defaultPacing || "safe");
    if (geminiMode === "manual" && !modelCfg.registry.some(m => m.id === geminiModel)) {
      setGeminiMode("auto");
      setGeminiModel("");
      showToast("Saved Gemini model ab verified list mein nahi hai — Auto mode set kiya", "info");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modelCfg]);

  const batchLimit = modelCfg?.batchLimits?.[provider] || modelCfg?.batchLimits?.default || DEFAULT_BATCH_LIMIT;
  useEffect(() => {
    if (!modelCfg) return;
    setBatchSize(b => Math.min(Math.max(b, batchLimit.min), batchLimit.max));
  }, [modelCfg, batchLimit.min, batchLimit.max]);

  useEffect(() => {
    try { localStorage.setItem(SETTINGS_STORAGE_KEY, JSON.stringify({ quality, pacing, batchSize })); } catch {}
  }, [quality, pacing, batchSize]);
  useEffect(() => {
    try { localStorage.setItem(GEMINI_STORAGE_KEY, JSON.stringify({ mode: geminiMode, model: geminiModel })); } catch {}
  }, [geminiMode, geminiModel]);

  const handleGeminiMode = (m) => {
    setGeminiMode(m);
    if (m === "manual" && !modelCfg?.registry?.some(x => x.id === geminiModel)) {
      setGeminiModel(modelCfg?.autoModels?.[0] || modelCfg?.registry?.[0]?.id || "");
    }
  };

  // Poll for new notifications every 30s (same cadence as the admin panel's auto-refresh)
  useEffect(() => {
    const t = setInterval(loadNotifications, 30000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    document.body.style.overflow = sidebarOpen ? "hidden" : "auto";
  }, [sidebarOpen]);

  const loadProfile = async () => {
    try {
      const res = await fetch(`${API}/api/profile`, {
        headers: { Authorization: "Bearer " + token }
      });
      if (!res.ok) return;
      const data = await res.json();
      setProfile(data);
    } catch {}
  };

  const loadNotifications = async () => {
    try {
      const res = await fetch(`${API}/api/notifications`, {
        headers: { Authorization: "Bearer " + token }
      });
      if (!res.ok) return;
      const data = await res.json();
      setNotifications(data.notifications || []);
      setUnreadCount(data.unreadCount || 0);
    } catch {}
  };

  const openNotifPanel = async () => {
    setShowNotifPanel(true);
    if (unreadCount > 0) {
      setUnreadCount(0); // optimistic -- panel is open, treat as read immediately
      try {
        await fetch(`${API}/api/notifications/mark-read`, {
          method: "POST",
          headers: { Authorization: "Bearer " + token }
        });
      } catch {}
    }
  };

  const loadFiles = async () => {
    try {
      const res = await fetch(`${API}/api/my-uploads`, {
        headers: { Authorization: "Bearer " + token }
      });
      const data = await res.json();
      setFiles(data.uploads || []);
    } catch { showToast("❌ Files load nahi hue", "error"); }
  };

  const uploadFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const upRes = await fetch(`${API}/api/upload`, {
        method: "POST",
        headers: { Authorization: "Bearer " + token },
        body: fd
      });
      const upData = await upRes.json();
      e.target.value = "";
      if (!upRes.ok) { showToast("❌ " + (upData.message || "Upload failed"), "error"); return; }
      showToast("✅ File upload ho gayi!", "success");
      loadFiles();
    } catch { showToast("❌ Upload failed", "error"); }
  };

  const deleteFile = async (id) => {
    if (!window.confirm("File delete karna chahte ho?")) return;
    setFiles(prev => prev.filter(f => f._id !== id));
    try {
      const res = await fetch(`${API}/api/delete/${id}`, {
        method: "DELETE",
        headers: { Authorization: "Bearer " + token }
      });
      if (!res.ok) { loadFiles(); showToast("❌ Delete failed", "error"); }
    } catch { loadFiles(); }
  };

  const convert = (id) => {
    if (!profile?.canUseAPI) {
      // Free tier (or expired plan): no longer a hard block. If they have a
      // banked ad-credit, let them through — the server charges it on the
      // first subtitle-texts fetch for this file. Otherwise, show the
      // watch-an-ad flow instead of immediately redirecting to /premium.
      if ((profile?.adCredits || 0) < 1) {
        setPendingConvertId(id);
        setShowAdModal(true);
        return;
      }
    }
    if (!apiKey) { showToast("Pehle API key daalo", "error"); return; }

    const file = files.find(f => f._id === id);

    // Model string for whichever 3rd-party provider is active (Gemini's model
    // comes from geminiMode / geminiModel below, so this stays undefined there).
    const activeModel =
      provider === "openrouter"  ? orModel :
      provider === "llama"       ? llamaModel :
      provider === "openai"      ? openaiModel :
      provider === "agentrouter" ? agentrouterModel :
      undefined;

    // Gemini Manual: the pinned model must be one the server's registry knows (the server re-validates anyway).
    const manualGemini = provider === "gemini" && geminiMode === "manual";
    if (manualGemini && !modelCfg?.registry?.some(m => m.id === geminiModel)) {
      showToast("Manual mode: pehle ek valid Gemini model chuno", "error");
      return;
    }
    // Same batch the slider shows, clamped to what the server accepts for this provider.
    const effBatch = Math.min(Math.max(batchSize, batchLimit.min), batchLimit.max);

    // Navigate to the live translation page
    navigate("/convert", {
      state: {
        fileId:     id,
        apiKey,
        provider,
        orModel:    activeModel,
        modelSelectionMode: provider === "gemini" ? geminiMode : undefined,
        geminiModel: manualGemini ? geminiModel : undefined,
        batchSize:  effBatch,
        maxBatch:   batchLimit.max,
        quality,
        pacing,
        sourceLang: sourceLang === "auto" ? null : sourceLang,
        fileName:   file?.fileName || "subtitle"
      }
    });
  }

  // Called by WatchAdModal after a successful claim — updates the local
  // profile's adCredits count and resumes the convert() flow that triggered it.
  const handleAdUnlocked = (newAdCredits) => {
    setProfile(prev => prev ? { ...prev, adCredits: newAdCredits } : prev);
    setShowAdModal(false);
    if (pendingConvertId) {
      const idToConvert = pendingConvertId;
      setPendingConvertId(null);
      setTimeout(() => convert(idToConvert), 300);
    }
  };

  const logout = async () => {
    const ok = await auth.logout();   // waits for the server to clear the cookie
    if (!ok) { showToast("❌ Logout nahi ho paya — internet check karo", "error"); return; }
    navigate("/login", { replace: true });
  };

  const planBadge = () => {
    const p = profile?.plan;
    if (p === "ultra_pro") return { label: "🌟 Ultra Pro", color: "#E8B34D" };
    if (p === "pro") return { label: "⚡ Pro", color: "#7C5CFF" };
    return { label: "🆓 Free", color: "#6E6E78" };
  };
  const badge = planBadge();

  return (
    <div style={styles.page}>

      {/* OVERLAY */}
      {sidebarOpen && (
        <div onClick={() => setSidebarOpen(false)} style={{
          position: "fixed", inset: 0,
          background: "rgba(0,0,0,0.65)",
          zIndex: 998, backdropFilter: "blur(2px)"
        }}/>
      )}

      {/* ══════ SIDEBAR ══════ */}
      <div style={{
        ...styles.sidebar,
        transform: sidebarOpen ? "translateX(0)" : "translateX(-100%)"
      }}>
        <div style={styles.sidebarLogoRow}>
          <SubAILogo />
        </div>

        {/* Nav Links + Report Bug — upar fixed */}
        <div style={styles.navLinks}>
          <div style={{ ...styles.navItem, background: "rgba(255,107,0,0.12)", color: "#F5F5F7", borderLeft: "3px solid #7C5CFF", borderRadius: 10 }}>
            🏠 Dashboard
          </div>
          <div style={styles.navItem} onClick={() => { navigate("/account"); setSidebarOpen(false); }}>👤 Account</div>
          <div style={styles.navItem} onClick={() => { navigate("/premium"); setSidebarOpen(false); }}>
            💎 Premium <span style={{ marginLeft: "auto", fontSize: 14 }}>👑</span>
          </div>
          <div style={styles.navItem} onClick={() => { navigate("/history"); setSidebarOpen(false); }}>📜 History</div>

          {/* Admin Panel — sirf admin ko dikhega */}
          {profile?.isAdmin && (
            <div
              style={{ ...styles.navItem, color: "#E8B34D", background: "rgba(255,215,0,0.07)", borderLeft: "3px solid #E8B34D", borderRadius: 10 }}
              onClick={() => { navigate("/admin"); setSidebarOpen(false); }}
            >
              👑 Admin Panel
            </div>
          )}

          <div style={{ borderTop: "1px solid #1E1E24", marginTop: 8, paddingTop: 8 }}>
            <div
              style={{ ...styles.navItem, color: "#FF8A93" }}
              onClick={() => { setBugModalOpen(true); setSidebarOpen(false); }}
            >
              🐛 Report Bug
            </div>
          </div>
        </div>

        {/* Spacer */}
        <div style={{ flex: 1 }} />

        {/* Ad — bilkul neeche */}
        {showAds && <div style={{ paddingBottom: 8 }}><Banner300x250 /></div>}
      </div>
      {/* SIDEBAR END */}

      {/* ══════ MAIN ══════ */}
      <div style={styles.main}>

        {/* Navbar */}
        <div style={styles.navbar}>
          <button onClick={() => setSidebarOpen(true)} style={styles.hamburger}>
            <span style={styles.hamLine}/>
            <span style={styles.hamLine}/>
            <span style={styles.hamLine}/>
          </button>
          <div style={styles.navLogo}><SubAILogo /></div>
          <button onClick={openNotifPanel} style={styles.bellBtn} aria-label="Notifications">
            🔔
            {unreadCount > 0 && (
              <span style={styles.bellBadge}>{unreadCount > 9 ? "9+" : unreadCount}</span>
            )}
          </button>
        </div>

        {/* ── Ad 1: NativeBanner — top of content, closeable nahi, natural ── */}
        {showAds && <NativeBannerAd />}

        {/* Upload Button */}
        <label style={styles.uploadBtn}>
          📤 Upload .srt / .vtt
          <input type="file" accept=".srt,.vtt,.ass,.ssa" onChange={uploadFile} style={{ display: "none" }}/>
        </label>

        {/* API Key box -- shown to ALL users, free and Pro alike. Free-tier
            users bring their own (free) Gemini/provider key; canUseAPI only
            controls whether ad-credits are required per file, not whether
            this input exists at all. */}
        <div style={styles.apiBox}>
            <h3 style={styles.sectionTitle}>🔑 API Key Settings</h3>

            {/* Provider selector -- custom animated chips (native <select> can't
                be styled/animated, and now that there are 4 providers instead
                of 2, a proper picker earns its keep). */}
            <div style={styles.providerRow}>
              {PROVIDERS.map((p, idx) => {
                const active   = provider === p.id;
                const isLoneLast = PROVIDERS.length % 2 === 1 && idx === PROVIDERS.length - 1;
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => setProvider(p.id)}
                    style={{
                      ...styles.providerChip,
                      ...(isLoneLast ? { gridColumn: "1 / -1" } : {}),
                      ...(active ? {
                        background: "#1E1E26",
                        borderColor: p.color,
                        boxShadow: `0 0 0 1px ${p.color}55, 0 4px 16px ${p.color}33`,
                      } : {})
                    }}
                  >
                    <span style={{
                      ...styles.providerIconBadge,
                      background: active ? `${p.color}22` : "#0B0B10",
                    }}>
                      {p.icon}
                    </span>
                    <span style={{
                      color: active ? p.color : "#9A9AA3",
                      fontWeight: active ? 700 : 500,
                      fontSize: 12.5,
                      lineHeight: 1.25,
                      textAlign: "left",
                    }}>
                      {p.label}
                    </span>
                  </button>
                );
              })}
            </div>

            <div style={{ position: "relative", marginTop: 10 }}>
              <input
                type={showKey ? "text" : "password"}
                value={apiKey}
                onChange={e => setApiKey(e.target.value)}
                placeholder={activeProviderConfig.keyPlaceholder}
                style={styles.input}
              />
              <span onClick={() => setShowKey(!showKey)} style={styles.eyeBtn}>
                {showKey ? "🙈" : "👁"}
              </span>
            </div>

            {/* Gemini model: Auto or Manual (list from the server's verified registry) */}
            {provider === "gemini" && (
              <GeminiModelPicker
                cfg={modelCfg}
                mode={geminiMode} onMode={handleGeminiMode}
                model={geminiModel} onModel={setGeminiModel}
                color={activeProviderConfig.color}
              />
            )}

            {/* OpenRouter model selector */}
            {provider === "openrouter" && (
              <div style={{ ...styles.modelPickerWrap, borderColor: activeProviderConfig.color + "33" }}>
                <label style={styles.modelPickerLabel}>
                  🤖 Model chuno (OpenRouter pe available)
                </label>
                <select
                  value={orModel}
                  onChange={e => setOrModel(e.target.value)}
                  style={{ ...styles.select, width: "100%", fontSize: 12 }}
                >
                  <optgroup label="🆓 Free Models (no credits needed)">
                    <option value="google/gemini-2.5-flash:free">Gemini 2.5 Flash — Free ⭐ Best</option>
                    <option value="google/gemini-2.0-flash-001:free">Gemini 2.0 Flash — Free</option>
                    <option value="meta-llama/llama-3.1-8b-instruct:free">Llama 3.1 8B — Free</option>
                    <option value="mistralai/mistral-7b-instruct:free">Mistral 7B — Free</option>
                    <option value="deepseek/deepseek-r1:free">DeepSeek R1 — Free (slow)</option>
                  </optgroup>
                  <optgroup label="💳 Paid Models (credits required)">
                    <option value="google/gemini-2.5-flash">Gemini 2.5 Flash — Paid (reliable)</option>
                    <option value="google/gemini-3-flash-preview">Gemini 3 Flash — Paid</option>
                    <option value="google/gemini-3.5-flash">Gemini 3.5 Flash — Paid</option>
                    <option value="google/gemini-3.6-flash">Gemini 3.6 Flash — Paid</option>
                    <option value="google/gemini-3.7-flash">Gemini 3.7 Flash — Paid (newest)</option>
                    <option value="google/gemini-3-pro-preview">Gemini 3 Pro — Paid</option>
                    <option value="google/gemini-3.1-pro-preview">Gemini 3.1 Pro — Paid</option>
                    <option value="anthropic/claude-3.5-haiku">Claude 3.5 Haiku — Paid (fast)</option>
                    <option value="anthropic/claude-3.5-sonnet">Claude 3.5 Sonnet — Paid</option>
                    <option value="openai/gpt-4o-mini">GPT-4o Mini — Paid</option>
                    <option value="openai/gpt-4o">GPT-4o — Paid</option>
                    <option value="openai/gpt-5">GPT-5 — Paid</option>
                    <option value="openai/gpt-5.4">GPT-5.4 — Paid</option>
                    <option value="openai/gpt-5.5">GPT-5.5 — Paid (frontier, expensive)</option>
                  </optgroup>
                </select>
                <p style={styles.modelPickerNote}>
                  ⭐ Free models ke liye sirf OpenRouter account chahiye — no credits! Free models kabhi kabhi busy ho jaate hain, retry karo ya paid try karo.
                </p>
              </div>
            )}

            {/* Llama AI (Meta, official) model selector */}
            {provider === "llama" && (
              <div style={{ ...styles.modelPickerWrap, borderColor: activeProviderConfig.color + "33" }}>
                <label style={styles.modelPickerLabel}>
                  🦙 Model chuno (Llama AI — Meta official)
                </label>
                <select
                  value={llamaModel}
                  onChange={e => setLlamaModel(e.target.value)}
                  style={{ ...styles.select, width: "100%", fontSize: 12 }}
                >
                  <option value="Llama-3.3-70B-Instruct">Llama 3.3 70B — Best quality</option>
                  <option value="Llama-3.3-8B-Instruct">Llama 3.3 8B — Faster, lighter</option>
                  <option value="Llama-4-Scout-17B-16E-Instruct-FP8">Llama 4 Scout — Newest</option>
                  <option value="Llama-4-Maverick-17B-128E-Instruct-FP8">Llama 4 Maverick — Newest, largest</option>
                </select>
                <p style={styles.modelPickerNote}>
                  ⚡ Agar chuna hua model available na ho to auto-switch ho jaayega agle model pe.
                </p>
              </div>
            )}

            {/* OpenAI model selector */}
            {provider === "openai" && (
              <div style={{ ...styles.modelPickerWrap, borderColor: activeProviderConfig.color + "33" }}>
                <label style={styles.modelPickerLabel}>
                  🤖 Model chuno (OpenAI)
                </label>
                <select
                  value={openaiModel}
                  onChange={e => setOpenaiModel(e.target.value)}
                  style={{ ...styles.select, width: "100%", fontSize: 12 }}
                >
                  <option value="gpt-4o-mini">GPT-4o Mini — Cheap, reliable</option>
                  <option value="gpt-4o">GPT-4o — Reliable</option>
                  <option value="gpt-5">GPT-5 — Strong reasoning</option>
                  <option value="gpt-5.4">GPT-5.4 — Frontier, expensive</option>
                </select>
                <p style={styles.modelPickerNote}>
                  ⚡ Agar chuna hua model available na ho to auto-switch ho jaayega agle model pe.
                </p>
              </div>
            )}

            {/* AgentRouter model selector */}
            {provider === "agentrouter" && (
              <div style={{ ...styles.modelPickerWrap, borderColor: activeProviderConfig.color + "33" }}>
                <label style={styles.modelPickerLabel}>
                  🧭 Model chuno (AgentRouter)
                </label>
                <select
                  value={agentrouterModel}
                  onChange={e => setAgentrouterModel(e.target.value)}
                  style={{ ...styles.select, width: "100%", fontSize: 12 }}
                >
                  <option value="gpt-5.6-sol">GPT-5.6 Sol</option>
                  <option value="claude-opus-5">Claude Opus 5</option>
                  <option value="claude-opus-4-8">Claude Opus 4.8</option>
                  <option value="deepseek-v4-flash">DeepSeek V4 Flash</option>
                  <option value="glm-5.3">GLM 5.3</option>
                </select>
                <p style={styles.modelPickerNote}>
                  ⚡ Agar chuna hua model available na ho to auto-switch ho jaayega agle model pe. Key yahan se lo: agentrouter.org/console/token
                </p>
              </div>
            )}

            <div style={{ ...styles.modelNotice, borderColor: activeProviderConfig.color + "33" }}>
              <span style={{ fontSize: 16 }}>{activeProviderConfig.icon}</span>
              <div>
                <span style={{ color: activeProviderConfig.color, fontWeight: 700 }}>{activeProviderConfig.label}</span>
                <span style={{ color: "#6E6E78" }}> — {activeProviderConfig.tagline}</span>
                <span style={{ display: "block", color: "#4A4A52", fontSize: 10, marginTop: 2 }}>
                  Key lene ke liye: <span style={{ color: activeProviderConfig.color }}>{activeProviderConfig.keyLink}</span>
                </span>
              </div>
            </div>
            <p style={styles.apiNote}>🔒 API key hum store nahi karte. Isse private rakhein — iski security aapki responsibility hai.</p>

            {/* ── TRANSLATION SETTINGS ── */}
            <TranslationSettings
              cfg={modelCfg} provider={provider}
              quality={quality} onQuality={setQuality}
              batchSize={batchSize} onBatch={setBatchSize}
              pacing={pacing} onPacing={setPacing}
              limit={batchLimit}
            />
          </div>

        {/* Upgrade hint for free users */}
        {!profile?.canUseAPI && (
          <div style={styles.adCreditBanner}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10 }}>
              <div>
                <p style={{ margin: 0, color: "#22D3C5", fontWeight: 800, fontSize: 14 }}>
                  💎 {profile?.adCredits || 0} free translation{profile?.adCredits === 1 ? "" : "s"} banked
                </p>
                <p style={{ margin: "2px 0 0", color: "#7E7E88", fontSize: 12 }}>
                  Har ad = 1 file translate. Jitne chaho utne dekho — unlimited.
                </p>
              </div>
              <button
                onClick={() => setShowAdModal(true)}
                style={styles.watchAdBtn}
              >
                📺 Ad Dekho, +1 Credit
              </button>
            </div>
          </div>
        )}

        {!profile?.canUseAPI && (
          <div onClick={() => navigate("/premium")} style={styles.upgradeBanner}>
            <span>⚡ Unlimited AI Translation ke liye Pro mein upgrade karo</span>
            <span style={{ color: "#7C5CFF" }}>→</span>
          </div>
        )}

        {/* Files List */}
        <h3 style={styles.sectionTitle}>📁 Tumhari Files ({files.length})</h3>

        {files.length === 0 && (
          <div style={styles.emptyBox}>
            <p style={{ color: "#57575F", fontSize: 14 }}>
              Koi file nahi hai. Upar se .srt ya .vtt file upload karo! 👆
            </p>
          </div>
        )}

        <div style={styles.fileGrid}>
          {files.map((f, idx) => (
            <div key={f._id}>
              <div style={styles.fileCard}>
                <div style={styles.fileIcon}>📄</div>
                <div style={styles.fileInfo}>
                  <b style={styles.fileName}>{f.fileName}</b>
                  <span style={styles.fileDate}>{new Date(f.createdAt).toLocaleDateString("en-IN")}</span>
                  {f.expiresAt && (() => {
                    const daysLeft = Math.ceil((new Date(f.expiresAt) - Date.now()) / (1000 * 60 * 60 * 24));
                    const urgent   = daysLeft <= 2;
                    return (
                      <span style={{
                        fontSize: 10, marginTop: 2, display: "block",
                        color: urgent ? "#F45B69" : "#86868F",
                        fontWeight: urgent ? 700 : 400,
                      }}>
                        {daysLeft <= 0 ? "⚠️ Aaj delete hogi" : urgent ? `⚠️ ${daysLeft} din bacha hai` : `⏳ ${daysLeft} din mein delete`}
                      </span>
                    );
                  })()}
                </div>
                <div style={styles.fileActions}>
                  <button onClick={() => navigate("/editor/" + f._id)} style={styles.actionBtn}>✏️ Edit</button>

                  {profile?.canUseAPI && (
                    <button
                      onClick={() => convert(f._id)}
                      disabled={false}
                      style={{ ...styles.actionBtn, background: "#7C5CFF22", color: "#7C5CFF", borderColor: "#7C5CFF" }}
                    >
                      {"⚡ Convert"}
                    </button>
                  )}

                  {!profile?.canUseAPI && (profile?.adCredits || 0) > 0 && (
                    <button
                      onClick={() => convert(f._id)}
                      style={{ ...styles.actionBtn, background: "#22D3C522", color: "#22D3C5", borderColor: "#22D3C5" }}
                    >
                      🎬 Free Translate
                    </button>
                  )}

                  {!profile?.canUseAPI && (profile?.adCredits || 0) === 0 && (
                    <button
                      onClick={() => { setPendingConvertId(f._id); setShowAdModal(true); }}
                      style={{ ...styles.actionBtn, background: "#E8B34D11", color: "#E8B34D", borderColor: "#E8B34D" }}
                    >
                      📺 Ad Dekho
                    </button>
                  )}

                  {f.convertedPath && (
                    <a
                      href={f.convertedPath}
                      download
                      style={{ ...styles.actionBtn, textDecoration: "none", background: "#34D39922", color: "#34D399", borderColor: "#34D399" }}
                    >
                      ⬇ Download
                    </a>
                  )}

                  <button
                    onClick={() => deleteFile(f._id)}
                    style={{ ...styles.actionBtn, background: "#F45B6922", color: "#F45B69", borderColor: "#F45B69" }}
                  >
                    🗑
                  </button>
                </div>
              </div>

              {/* ── Ad 2: NativeBanner — har 4th file ke baad, natural break ── */}
              {showAds && (idx + 1) % 4 === 0 && idx !== files.length - 1 && (
                <div style={{ margin: "12px 0" }}>
                  <NativeBannerAd />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* ━━━ TOAST NOTIFICATIONS ━━━ */}
      <div style={{
        position: "fixed", bottom: 80, left: "50%",
        transform: "translateX(-50%)",
        zIndex: 9999,
        display: "flex", flexDirection: "column", gap: 8,
        width: "90%", maxWidth: 360, pointerEvents: "none"
      }}>
        {toasts.map(t => (
          <div key={t.id} style={{
            background: t.type === "success" ? "#12241C" : t.type === "error" ? "#1F1418" : "#18162A",
            border: `1px solid ${t.type === "success" ? "#34D39966" : t.type === "error" ? "#F45B6966" : "#7C5CFF66"}`,
            borderRadius: 12,
            padding: "12px 16px",
            color: t.type === "success" ? "#34D399" : t.type === "error" ? "#FF8A93" : "#A8A8B3",
            fontSize: 13,
            fontWeight: 600,
            boxShadow: "0 4px 20px rgba(0,0,0,0.5)",
            lineHeight: 1.4,
          }}>
            {t.msg}
          </div>
        ))}
      </div>

      <style>{`
        @keyframes slideUp {
          from { opacity:0; transform:translateY(16px); }
          to   { opacity:1; transform:translateY(0); }
        }
        @keyframes notifSlideIn {
          from { transform:translateX(100%); }
          to   { transform:translateX(0); }
        }
      `}</style>

      {/* ══════ BUG REPORT MODAL ══════ */}
      {bugModalOpen && (
        <div style={{
          position: "fixed", inset: 0, zIndex: 2000,
          background: "rgba(0,0,0,0.75)", backdropFilter: "blur(4px)",
          display: "flex", alignItems: "center", justifyContent: "center",
          padding: "0 16px"
        }}>
          <div style={{
            background: "#0B0B10", border: "1px solid #26262E",
            borderRadius: 16, padding: 24, width: "100%", maxWidth: 420
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
              <h3 style={{ margin: 0, color: "#F5F5F7", fontSize: 17 }}>🐛 Report a Bug</h3>
              <span onClick={() => { setBugModalOpen(false); setBugMessage(""); }}
                style={{ color: "#86868F", fontSize: 20, cursor: "pointer" }}>✕</span>
            </div>
            <p style={{ color: "#7E7E88", fontSize: 13, margin: "0 0 14px 0" }}>
              Koi bhi problem describe karo — hum jaldi fix karenge!
            </p>
            <textarea
              value={bugMessage}
              onChange={e => setBugMessage(e.target.value)}
              placeholder="Bug ka description likho yahan... (kya hua, kab hua, kaunsa device)"
              rows={5}
              style={{
                width: "100%", background: "#131318", border: "1px solid #38383F",
                color: "#D6D6DC", padding: "10px 12px", borderRadius: 10,
                fontSize: 14, resize: "vertical", outline: "none",
                boxSizing: "border-box", fontFamily: "sans-serif"
              }}
            />
            <button
              onClick={async () => {
                if (!bugMessage.trim()) { showToast("Pehle bug describe karo!", "error"); return; return; }
                setBugSubmitting(true);
                try {
                  const res = await fetch(`${API}/api/bug-report`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json", Authorization: "Bearer " + token },
                    body: JSON.stringify({ message: bugMessage, email: profile?.email || "unknown" })
                  });
                  if (res.ok) {
                    showToast("✅ Bug report bhej diya! Thanks!", "success");
                    setBugModalOpen(false);
                    setBugMessage("");
                  } else {
                    showToast("❌ Report nahi bheja", "error");
                  }
                } catch { showToast("❌ Network error", "error"); }
                finally { setBugSubmitting(false); }
              }}
              disabled={bugSubmitting}
              style={{
                marginTop: 12, width: "100%", padding: "12px",
                background: bugSubmitting ? "#38383F" : "linear-gradient(135deg, #7C5CFF, #6244D9)",
                color: "#F5F5F7", border: "none", borderRadius: 10,
                fontWeight: 700, fontSize: 15, cursor: bugSubmitting ? "not-allowed" : "pointer"
              }}
            >
              {bugSubmitting ? "⏳ Bhej raha hai..." : "📤 Report Bhejo"}
            </button>
          </div>
        </div>
      )}

      {/* ══════ WATCH-AD MODAL (free tier unlock) ══════ */}
      <WatchAdModal
        open={showAdModal}
        token={token}
        onClose={() => { setShowAdModal(false); setPendingConvertId(null); }}
        onUnlocked={handleAdUnlocked}
      />

      {/* ══════ NOTIFICATIONS PANEL ══════ */}
      {showNotifPanel && (
        <div style={styles.notifOverlay} onClick={() => setShowNotifPanel(false)}>
          <div style={styles.notifPanel} onClick={e => e.stopPropagation()}>
            <div style={styles.notifHeader}>
              <span style={{ fontWeight: 800, fontSize: 15 }}>🔔 Notifications</span>
              <button onClick={() => setShowNotifPanel(false)} style={styles.notifCloseBtn}>✕</button>
            </div>
            <div style={styles.notifList}>
              {notifications.length === 0 && (
                <div style={{ padding: "30px 16px", textAlign: "center", color: "#57575F", fontSize: 12 }}>
                  Koi notification nahi hai abhi
                </div>
              )}
              {notifications.map(n => (
                <div key={n._id} style={styles.notifItem}>
                  <span style={{ fontSize: 16 }}>{n.type === "system" ? "🚀" : "📢"}</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ color: "#D6D6DC", fontSize: 12.5, lineHeight: 1.5 }}>{n.message}</div>
                    <div style={{ color: "#57575F", fontSize: 10, marginTop: 4 }}>
                      {new Date(n.createdAt).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

const styles = {
  page: { display: "flex", minHeight: "100vh", background: "#0B0B10", color: "#D6D6DC", fontFamily: "sans-serif" },

  sidebar: {
    width: 280,
    position: "fixed", left: 0, top: 0, height: "100vh",
    display: "flex", flexDirection: "column",
    overflowY: "hidden",
    transform: "translateX(-100%)",
    transition: "transform 0.3s cubic-bezier(0.4,0,0.2,1)",
    zIndex: 1000,
    background: "#0B0B10", borderRight: "1px solid #20202A",
    padding: "20px 16px 0 16px", gap: 10, boxSizing: "border-box",
  },
  sidebarScrollArea: {
    flex: 1,
    overflowY: "auto",
    display: "flex",
    flexDirection: "column",
    gap: 10,
    minHeight: 0,
  },
  sidebarLogoRow: { marginBottom: 8 },
  navLinks: { display: "flex", flexDirection: "column", gap: 4 },
  navItem: {
    padding: "11px 14px", borderRadius: 10, cursor: "pointer",
    color: "#86868F", fontSize: 15, display: "flex", alignItems: "center",
    gap: 10, transition: "background 0.15s"
  },

  navbar: {
    display: "flex", alignItems: "center", justifyContent: "space-between",
    padding: "12px 16px", borderBottom: "1px solid #131318",
    marginBottom: 20, position: "sticky", top: 0, zIndex: 100,
    background: "#0B0B10"
  },
  hamburger: {
    background: "none", border: "none", cursor: "pointer",
    display: "flex", flexDirection: "column", gap: 5,
    padding: 4, width: 40, alignItems: "flex-start"
  },
  hamLine: { display: "block", height: 2, borderRadius: 2, background: "#D6D6DC", width: 24 },
  navLogo: { display: "flex", alignItems: "center" },

  bellBtn: {
    position: "relative", width: 40, height: 40,
    display: "flex", alignItems: "center", justifyContent: "center",
    background: "none", border: "none", cursor: "pointer",
    fontSize: 19, WebkitTapHighlightColor: "transparent",
  },
  bellBadge: {
    position: "absolute", top: 2, right: 2,
    background: "#7C5CFF", color: "#F5F5F7",
    fontSize: 9, fontWeight: 800, lineHeight: 1,
    minWidth: 15, height: 15, borderRadius: 8,
    display: "flex", alignItems: "center", justifyContent: "center",
    padding: "0 3px", border: "2px solid #0B0B10",
  },

  notifOverlay: {
    position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)",
    zIndex: 1200, display: "flex", justifyContent: "flex-end",
  },
  notifPanel: {
    width: "100%", maxWidth: 360, height: "100%",
    background: "#131318", borderLeft: "1px solid #26262E",
    display: "flex", flexDirection: "column",
    animation: "notifSlideIn 0.25s ease-out",
  },
  notifHeader: {
    display: "flex", alignItems: "center", justifyContent: "space-between",
    padding: "18px 16px", borderBottom: "1px solid #26262E", color: "#F5F5F7",
  },
  notifCloseBtn: {
    background: "none", border: "none", color: "#86868F",
    fontSize: 16, cursor: "pointer", padding: 4,
  },
  notifList: { flex: 1, overflowY: "auto" },
  notifItem: {
    display: "flex", gap: 10, alignItems: "flex-start",
    padding: "14px 16px", borderBottom: "1px solid #20202A",
  },

  main: { flex: 1, padding: "0 16px 40px 16px", width: "100%" },

  uploadBtn: {
    display: "block", width: "100%", textAlign: "center",
    background: "linear-gradient(135deg, #7C5CFF, #6244D9)",
    color: "#F5F5F7", padding: "14px", borderRadius: 9999,
    cursor: "pointer", fontWeight: 700, fontSize: 14,
    textTransform: "uppercase", letterSpacing: 0.6,
    marginBottom: 20, boxSizing: "border-box",
    boxShadow: "0 8px 20px rgba(124,92,255,0.25)"
  },
  adCreditBanner: {
    background: "#0E1F1E", border: "1px solid #22D3C544",
    borderRadius: 10, padding: "14px 16px",
    marginBottom: 12,
  },
  watchAdBtn: {
    background: "linear-gradient(135deg,#22D3C5,#1AA89D)", border: "none",
    borderRadius: 9999, padding: "10px 16px", color: "#0B0B10",
    fontWeight: 800, fontSize: 13, cursor: "pointer", whiteSpace: "nowrap",
  },
  upgradeBanner: {
    background: "#1B1730", border: "1px solid #7C5CFF44",
    borderRadius: 10, padding: "12px 16px",
    display: "flex", justifyContent: "space-between", alignItems: "center",
    marginBottom: 20, cursor: "pointer", color: "#A8A8B3", fontSize: 14
  },
  apiBox: { background: "#131318", border: "1px solid #26262E", borderRadius: 12, padding: 18, marginBottom: 20 },
  sectionTitle: { color: "#7C5CFF", margin: "0 0 12px 0", fontSize: 13, textTransform: "uppercase", letterSpacing: 1 },
  input: { width: "100%", background: "#20202A", border: "1px solid #38383F", color: "#D6D6DC", padding: "10px 40px 10px 12px", borderRadius: 8, fontSize: 14, outline: "none", boxSizing: "border-box" },
  eyeBtn: { position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", cursor: "pointer", fontSize: 16 },
  select: { background: "#20202A", border: "1px solid #38383F", color: "#D6D6DC", padding: "10px 12px", borderRadius: 8, fontSize: 14, cursor: "pointer" },
  apiNote: { color: "#57575F", fontSize: 11, margin: "4px 0 0 0" },

  // -- Provider picker: 2-column grid of cards, replaces the plain native <select> --
  providerRow: {
    display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10,
    marginTop: 4,
  },
  providerChip: {
    display: "flex", alignItems: "center", gap: 8,
    padding: "12px 14px", borderRadius: 12,
    background: "#131318", border: "1.5px solid #26262E",
    cursor: "pointer",
    transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
    outline: "none",
    WebkitTapHighlightColor: "transparent",
  },
  providerIconBadge: {
    display: "flex", alignItems: "center", justifyContent: "center",
    width: 30, height: 30, borderRadius: 9,
    background: "#0B0B10", flexShrink: 0,
    fontSize: 15,
    transition: "background 0.25s ease",
  },

  // -- Model picker card, shown under the chosen provider --
  modelPickerWrap: {
    marginTop: 14, padding: 14, borderRadius: 12,
    background: "#131318", border: "1px solid #26262E",
  },
  modelPickerLabel: {
    color: "#A8A8B3", fontSize: 11, fontWeight: 600,
    display: "block", marginBottom: 8, letterSpacing: 0.3,
  },
  modelPickerNote: { color: "#57575F", fontSize: 10.5, margin: "8px 0 0", lineHeight: 1.5 },

  modelNotice: {
    display: "flex", alignItems: "center", gap: 10,
    background: "#241C10", border: "1px solid #E8B34D33",
    borderRadius: 8, padding: "8px 12px", marginBottom: 4,
  },
  emptyBox: { background: "#0B0B10", border: "2px dashed #1E1E24", borderRadius: 12, padding: 40, textAlign: "center" },
  fileGrid: { display: "flex", flexDirection: "column", gap: 10 },
  fileCard: { background: "#131318", border: "1px solid #1E1E24", borderRadius: 12, padding: "14px 18px", display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" },
  fileIcon: { fontSize: 22, flexShrink: 0 },
  fileInfo: { flex: 1, minWidth: 100 },
  fileName: { display: "block", fontSize: 13, marginBottom: 2 },
  fileDate: { color: "#57575F", fontSize: 12 },
  fileActions: { display: "flex", gap: 8, flexWrap: "wrap" },
  actionBtn: { background: "#20202A", border: "1px solid #38383F", color: "#A8A8B3", padding: "6px 12px", borderRadius: 8, cursor: "pointer", fontSize: 13, textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 4 }
};
